import { type Event } from '@goodneighbor/api/schemas/events/event.schema';
import { getEventStatus, type EventStatus } from '@goodneighbor/ui';
import {
  formatTimeRangeAndTimezone,
  getShortTimeWithTimezone
} from '@goodneighbor/utilities/time/index';
import {
  typesOfOutreachActivity,
  preferredSkills,
  causeTypes
} from '@goodneighbor/api/routes/events/organization/registration/eventsOrganizationRegistration.constants';
import Image from 'next/image';

export const skillTypeMap = {
  [preferredSkills.GENERAL_VOLUNTEER]: 'General',
  [preferredSkills.HANDYMAN]: 'Handyman',
  [preferredSkills.PAINTING]: 'Painting',
  [preferredSkills.ADMINISTRATION]: 'Administration',
  [preferredSkills.HOSPITALITY]: 'Hospitality',
  [preferredSkills.LANDSCAPING]: 'Landscaping',
  [preferredSkills.BILINGUAL_SPEAKERS]: 'Interpreters (Spanish/English)',
  [preferredSkills.OTHER]: 'Other'
};

export const activityTypeMap = {
  [typesOfOutreachActivity.BEAUTIFICATION]: 'Beautification',
  [typesOfOutreachActivity.FOOD_DISTRIBUTION]: 'Food distribution',
  [typesOfOutreachActivity.GIFTS_IN_KIND_DISTRIBUTION]:
    'Gifts-In-Kind Distribution',
  [typesOfOutreachActivity.NEIGHBORHOOD_CANVASSING]: 'Neighborhood canvassing',
  [typesOfOutreachActivity.HEALTH_CLINIC]: 'Health clinic',
  [typesOfOutreachActivity.SPORTS_CAMP]: 'Sports camp',
  [typesOfOutreachActivity.FESTIVAL_BLOCK_PARTY]: 'Festival / block party',
  [typesOfOutreachActivity.NON_PROFIT_PARTNERSHIP]: 'Non-profit Partnership',
  [typesOfOutreachActivity.OTHER]: 'Other'
};

export const causeTypeMap = {
  [causeTypes.ABUSE_AND_HUMAN_TRAFFICKING]: 'Abuse & human trafficking',
  [causeTypes.CHILDREN_AND_YOUTH]: 'Children & youth',
  [causeTypes.COMMUNITY_DEVELOPMENT]: 'Community development',
  [causeTypes.DISASTER_RELIEF]: 'Disaster relief',
  [causeTypes.EDUCATIONAL_SUPPORT]: 'Educational support',
  [causeTypes.ELDERLY_CARE]: 'Elderly care',
  [causeTypes.FAMILIES_IN_CRISIS]: 'Families in crisis',
  [causeTypes.HEALTH_AND_MEDICINE]: 'Health & medicine',
  [causeTypes.HOUSING_AND_HOMELESSNESS]: 'Housing & homelessness',
  [causeTypes.HUNGER_FOOD_SECURITY]: 'Hunger, food security',
  [causeTypes.IMMIGRANTS_OR_REFUGEES]: 'Immigrants or refugees',
  [causeTypes.INCARCERATION_AND_REENTRY_SUPPORT]:
    'Incarceration and re-entry support',
  [causeTypes.JOB_AND_WORKPLACE]: 'Job & workplace',
  [causeTypes.MENTAL_HEALTH]: 'Mental health',
  [causeTypes.NEURODIVERSITY_AND_DEVELOPMENTAL_DISABILITIES]:
    'Neurodiversity & developmental disabilities',
  [causeTypes.PHYSICAL_DISABILITIES]: 'Physical disabilities',
  [causeTypes.POVERTY]: 'Poverty',
  [causeTypes.SINGLE_MOMS]: 'Single moms',
  [causeTypes.SPORTS_AND_RECREATION]: 'Sports & recreation',
  [causeTypes.VETERANS]: 'Veterans',
  [causeTypes.VICTIM_SUPPORT]: 'Victim support',
  [causeTypes.OTHER]: 'Other'
};

interface ParseForEventCardPropsOptions {
  /** Whether to include the event status badge. Defaults to false for public views. */
  showStatus?: boolean;
  hideEndTime?: boolean;
}

const getEventCardDateParts = (
  dateString: string,
  timeZoneId?: string | null
): { months: string; dates: string; year: string } | undefined => {
  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: timeZoneId || 'UTC'
  });

  const parts = formatter.formatToParts(date);
  const months = parts.find((part) => part.type === 'month')?.value;
  const dates = parts.find((part) => part.type === 'day')?.value;
  const year = parts.find((part) => part.type === 'year')?.value;

  if (!months || !dates || !year) {
    return undefined;
  }

  return { months, dates, year };
};

export const getEventCardDate = (
  event: Pick<Event, 'dateDisplay' | 'dates' | 'startsAt' | 'timeZoneId'>
) => {
  if (event.dateDisplay) {
    return {
      months: event.dateDisplay.months,
      dates: event.dateDisplay.dates,
      year: event.dates?.[0]?.startsAt
        ? new Date(event.dates[0].startsAt).getFullYear().toString()
        : undefined
    };
  }

  const fallbackStartsAt = event.startsAt || event.dates?.[0]?.startsAt;

  if (!fallbackStartsAt) {
    return undefined;
  }

  return getEventCardDateParts(fallbackStartsAt, event.timeZoneId);
};

export const parseForEventCardProps = (
  event: Event,
  organizationName: string,
  options: ParseForEventCardPropsOptions = {}
) => {
  const timeRange =
    event.startsAt && event.endsAt && event.timeZoneId
      ? formatTimeRangeAndTimezone(
          new Date(event.startsAt),
          new Date(event.endsAt),
          event.timeZoneId
        )
      : '-';
  const { showStatus = false } = options;
  return {
    title: event.title,
    organizationName,
    location:
      event.city && event.region
        ? `${event.city}, ${event.region}`
        : event.locationName || undefined,
    category: event.categoryName || undefined,
    date: getEventCardDate(event),
    time:
      options.hideEndTime && event.startsAt && event.timeZoneId
        ? getShortTimeWithTimezone(new Date(event.startsAt), event.timeZoneId)
        : timeRange,
    media: event.promoImageUrl ? (
      <Image
        src={event.promoImageUrl}
        alt={event.title}
        fill
        style={{ objectFit: 'cover' }}
      />
    ) : undefined,
    status: showStatus
      ? getEventStatus(
          event.isApproved,
          event.overallCapacity,
          event.spotsTaken,
          event.startsAt
        )
      : undefined
  };
};

/**
 * Calculates the event status for a given event.
 * Re-exported for convenience.
 */
export { getEventStatus, type EventStatus };
